import { GetExecutionSummary, ExportJobExecutionDetails } from '../../api/executions-endpoint';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ExecutionSummary } from '../../types/execution';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid';
import useConfigStyles from '../../components/useConfigStyles';
import { defaultExecutionSummary } from '../../types/defaultValues';
import { Link } from 'react-router-dom';
import { getLinkToExecutionRuleFailuresPage, EXECUTIONS } from '../../navigator/links';
import Box from '@mui/material/Box';
import AcceptButton from '../../components/AcceptButton';

function ExecutionSummaryPage(): JSX.Element {
  const { jobName } = useParams<{ jobName: string }>();
  const { logId } = useParams<{ logId: string }>();
  const classes = useConfigStyles();
  const [execution, setExecution] = useState<ExecutionSummary>(defaultExecutionSummary);
  const [fetched, setFetched] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const fetchExecution = useCallback(async () => {
    const response = await GetExecutionSummary(jobName, logId);
    if (response.success && response.data) {
      setExecution(response.data);
    } else {
      setResponseMessage(response.message || '');
    }
    setFetched(response.success);
  }, [jobName, logId]);

  useEffect(() => {
    fetchExecution();
  }, [fetchExecution]);

  const handleExportClick = async () => {
    const response = await ExportJobExecutionDetails(jobName, logId);
    if (response.success && response.data) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${jobName}.csv`);
      document.body.appendChild(link);
      link.click();
    }
  };

  const exportButton = () => {
    const rulesIssues = execution.Rules.map((rule) => rule.Issues).reduce((sum, current) => sum + current, 0);

    return rulesIssues > 0 ? <AcceptButton className={classes.button} label={'Export All Details to CSV'} onClick={handleExportClick} /> : null;
  };

  return !fetched ? (
    <span>{responseMessage}</span>
  ) : (
    <Container>
      <Breadcrumbs className={classes.breadcrumbs}>
        <Link to={EXECUTIONS}>Executions</Link>
        <Typography color='textPrimary'>{jobName}</Typography>
      </Breadcrumbs>
      <h1>Job Summary</h1>
      <Grid container direction='column' justifyContent='center' alignItems='stretch' spacing={'1vw'}>
        <Box sx={{ width: '60%', mb: 4 }}>
          <TableContainer component={Paper}>
            <Table className={classes.table}>
              <TableBody>
                <TableRow>
                  <TableCell variant='head' className={classes.tableHeader}>
                    Job Status
                  </TableCell>
                  <TableCell>{execution.Status}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant='head' className={classes.tableHeader}>
                    Job Name
                  </TableCell>
                  <TableCell>{jobName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant='head' className={classes.tableHeader}>
                    Job Date
                  </TableCell>
                  <TableCell>{new Date(execution.ExecutedAt).toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label='simple table'>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell>Rule</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Records Found</TableCell>
                <TableCell>View Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {execution.Rules.map((rule) => (
                <TableRow key={rule.Rule} className={classes.tableRow}>
                  <TableCell component='td' scope='row'>
                    {rule.Rule}
                  </TableCell>
                  <TableCell component='td' scope='row'>
                    {rule.Severity}
                  </TableCell>
                  <TableCell component='td' scope='row'>
                    {rule.Description}
                  </TableCell>
                  <TableCell component='td' scope='row'>
                    {rule.Issues}
                  </TableCell>
                  <TableCell component='td' scope='row'>
                    {rule.Issues != 0 && <Link to={getLinkToExecutionRuleFailuresPage(execution.Job, logId!, rule.Rule)}>Details</Link>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid item>{exportButton()}</Grid>
      </Grid>
    </Container>
  );
}

export default ExecutionSummaryPage;
